import React from 'react';

const Terms = () => {
  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <div className="shadow-md rounded-2xl p-8 max-w-4xl bg-[#112D4E]">
        <h1 className="text-3xl font-bold mb-6">
          Terms and <span className="text-[#19BFB7]">Conditions</span>
        </h1>
        <p className="mb-4">Effective Date: 2025</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">1. Introduction</h2>
          <p>
            Welcome to <strong>AIAURA</strong> ("Company," "we," "our," or "us"). These Terms and Conditions ("Terms") govern your use of our website and services. By accessing or using AIAURA, you agree to be bound by these Terms. If you do not agree, please refrain from using our website.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">2. Definitions</h2>
          <ul className="list-disc list-inside">
            <li><strong>AIAURA</strong> – The AI-driven recruitment platform operated by the Company.</li>
            <li><strong>User</strong> – Any individual or entity accessing or using AIAURA.</li>
            <li><strong>Employer</strong> – A business or individual using AIAURA to post jobs or recruit candidates.</li>
            <li><strong>Recruiter</strong> – A third party, such as a staffing agency, using AIAURA to facilitate recruitment on behalf of employers.</li>
            <li><strong>Candidate</strong> – A job seeker using AIAURA to search for employment opportunities.</li>
            <li><strong>RTW Checks</strong> – Verification of a candidate’s legal right to work in a particular jurisdiction.</li>
            <li><strong>AI-Generated Insights</strong> – Recommendations, analyses, or suggestions provided by AIAURA’s AI systems.</li>
            <li><strong>Account</strong> – A registered user profile on AIAURA.</li>
            <li><strong>Third-Party Services</strong> – Any external websites, applications, or services linked to or integrated with AIAURA.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">3. Eligibility</h2>
          <p>
            To use AIAURA, you must be at least 18 years old and have the legal capacity to enter into a binding agreement. By using our services, you represent and warrant that you meet these requirements.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">4. Services Provided</h2>
          <p>
            AIAURA is an AI-driven recruitment platform that connects job seekers and employers. We do not guarantee employment or hiring outcomes. The information and recommendations provided by our AI system should not be construed as professional career or legal advice.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">5. Compliance with UK & EU Recruitment Laws</h2>
          <p>
            AIAURA operates in compliance with UK and EU employment and recruitment laws, including but not limited to the Employment Agencies Act 1973, the Conduct of Employment Agencies and Employment Businesses Regulations 2003, and the GDPR. AIAURA does not act as an employment agency or employment business and does not directly introduce or supply candidates to employers for employment. Employers and recruiters using AIAURA must comply with all applicable legal obligations when hiring candidates, including anti-discrimination laws, data protection regulations, and right-to-work checks. AIAURA is not responsible for ensuring users comply with recruitment regulations and disclaims all liability for non-compliance by users.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">6. Account Registration and Security</h2>
          <ul className="list-disc list-inside">
            <li>You must provide accurate and complete information during registration.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You must notify us immediately of any unauthorized use of your account.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">7. User Responsibilities</h2>
          <ul className="list-disc list-inside">
            <li>Provide truthful and up-to-date information.</li>
            <li>Use the platform for lawful purposes only.</li>
            <li>Not engage in fraudulent, misleading, or abusive behavior.</li>
            <li>Not upload or distribute harmful content, including malware or illegal material.</li>
            <li>Respect the intellectual property rights of others.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">8. Right to Work (RTW) and Credential Verification</h2>
          <p>
            AIAURA does not conduct Right to Work (RTW) checks or verify the authenticity of candidates’ credentials. Employers and recruiters are solely responsible for conducting necessary background checks, employment eligibility verifications, and credential authentication before making hiring decisions. AIAURA is not liable for any inaccuracies or misrepresentations in candidate-provided information. Employers and recruiters must ensure compliance with UK and EU employment laws, including the Immigration, Asylum and Nationality Act 2006 and any applicable right-to-work regulations.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">9. Fees and Payments</h2>
          <ul className="list-disc list-inside">
            <li>Certain features of AIAURA may require payment. By using paid services, you agree to pay all applicable fees as stated at the time of purchase.</li>
            <li>You also agree to allow us to process payments through our designated payment providers.</li>
            <li>No refunds will be issued unless required by law.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">10. Privacy and Data Protection</h2>
          <p>
            Your use of AIAURA is also governed by our Privacy Policy, which details how we collect, use, and protect your data. We comply with the UK General Data Protection Regulation (UK GDPR) and EU GDPR, ensuring data security and user privacy. By using our services, you consent to our data practices.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">11. Intellectual Property Rights</h2>
          <p>
            AIAURA and its content (including logos, software, and designs) are owned by or licensed to us and protected by intellectual property laws. You may not copy, modify, distribute, or use our content without permission. You retain ownership of any content you submit but grant us a license to use it for platform functionality.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">12. AI Decision-Making Disclaimer</h2>
          <p>
            AIAURA provides AI-generated insights but does not make final hiring decisions. AI-generated recommendations are supplementary tools, and employers should apply independent judgment. AIAURA is not liable for any hiring decisions made based on AI-generated insights.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">13. Bias-Free AI Policy</h2>
          <p>
            AIAURA is committed to reducing bias in its AI-driven recruitment platform. Our approach includes: using diverse and representative training data to mitigate biases; conducting regular audits and testing of AI models for fairness; allowing user feedback to address potential biases and improve system performance; and employing fairness-aware algorithms where applicable. AIAURA is not liable for any unintended biases that may occur in AI-generated insights, recommendations, or hiring outcomes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">14. Acceptable Use Policy</h2>
          <p>
            Users must not use AIAURA for illegal, harmful, or fraudulent purposes. Prohibited activities include spamming, harassment, and unauthorized access to the platform. Violation of this policy may result in account suspension or legal action.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">15. Disclaimer of Warranties</h2>
          <p>
            AIAURA is provided "as is" without warranties of any kind. We do not guarantee uninterrupted or error-free service, that our platform will meet all your expectations or needs, or the accuracy of AI-generated insights or recommendations.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">16. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, AIAURA and its affiliates will not be liable for indirect, incidental, or consequential damages, loss of data, profits, or opportunities, or any actions taken based on AI-generated suggestions.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">17. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless AIAURA from any claims, damages, or expenses arising from your violation of these Terms or misuse of the platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">18. Modifications to Terms and Platform</h2>
          <p>
            We reserve the right to update or modify these Terms at any time. Changes will be posted on our website with an updated "Effective Date." Continued use of AIAURA after modifications constitutes acceptance of the revised Terms. AIAURA may update, enhance, or add new features to its software as it develops and grows. These updates may modify or improve functionality, and users agree to accept such changes as part of their continued use of the platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">19. Governing Law & Dispute Resolution</h2>
          <p>
            These Terms are governed by the laws of England and Wales. Any disputes shall first be resolved through mediation. If mediation fails, disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">20. Contact Information</h2>
          <p>
            For questions regarding these Terms, please contact us at <a href="mailto:admin@aiaura.co.uk" className="text-[#19BFB7]">admin@aiaura.co.uk</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Terms;
